import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ComponentProps } from 'app/types/dropdown';

import { getUniqueKey } from './helpers';

export function withDropdown<P>(WrappedComponent: React.ComponentType<P & ComponentProps>) {
  const NewComponent = (props: P) => {
    const [active, setActive] = useState<boolean>(false);
    const nodes: ChildNode[] = useMemo(() => [], []);

    const dynamicId = `dropdown-${getUniqueKey()}`;

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const handleDropdown = useCallback((e: MouseEvent) => {
      if (e.target === inputRef.current) {
        setActive(true);
      }

      if (active && !nodes.includes(e.target as ChildNode)) {
        setActive(false);
      }

      return undefined;
    }, [active, nodes]);

    const getAllChildNodes = useCallback((parentNode: ChildNode) => {
      if (parentNode.hasChildNodes()) {
        for (let i = 0; i < parentNode.childNodes.length; i++) {
          nodes.push(parentNode.childNodes[i]);
          getAllChildNodes(parentNode.childNodes[i]);
        }
      } else {
        return undefined;
      }

      return undefined;
    }, [nodes]);

    useLayoutEffect(() => {
      const body = document.querySelector('body');
      const root = document.getElementById(dynamicId);

      if (root) {
        getAllChildNodes(root);
      }

      body?.addEventListener('click', handleDropdown);

      return () => body?.removeEventListener('click', handleDropdown);
    }, [dynamicId, getAllChildNodes, handleDropdown]);

    return (
      <WrappedComponent
        dropdownRef={dropdownRef}
        inputRef={inputRef}
        active={active}
        setActive={setActive}
        dynamicId={dynamicId}
        {...props}
      />
    );
  };

  return NewComponent;
}
